import React from "react";
import { TbAlertCircle, TbAlertTriangle, TbFileOff, TbSparkles, TbX } from "react-icons/tb";
import { FiRefreshCw } from "react-icons/fi";
import { LuFileWarning } from "react-icons/lu";

import {
  SmartNegotiationCard,
  CardType,
  CounterCard,
  FlagsCard,
  CaseSummaryCard,
  MoneyCapacityCard,
  BulletListCard,
  FlagSeverity,
  TagItem,
  TagVariant,
  BulletListVariant,
  PanelStatus,
} from "dtos/smart-negotiation.dto";
import { ApiService } from "api/api.service";
import { getErrorMessage } from "utils/functions";
import { Button, Drawer } from "antd";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatUSD(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ─── Card filter — decides whether a card should render at all ────────────────
//     Called before the wrapper div renders, so no empty white boxes appear.

function shouldRenderCard(card: SmartNegotiationCard): boolean {
  switch (card.type) {
    case CardType.COUNTER:
      // skip only when BOTH amounts are null
      return card.ourAmount !== null || card.theirAmount !== null;
    case CardType.FLAGS:
      return card.flags.length > 0;
    case CardType.BULLET_LIST:
      return card.items.length > 0;
    default:
      return true;
  }
}

// ─── Flag icon map ────────────────────────────────────────────────────────────

const FLAG_ICON: Record<FlagSeverity, React.ReactNode> = {
  [FlagSeverity.DEADLINE]: <TbAlertCircle className="w-[15px] h-[15px] text-red-500   mt-0.5 shrink-0" />,
  [FlagSeverity.FINANCIAL]: <TbAlertTriangle className="w-[15px] h-[15px] text-amber-500 mt-0.5 shrink-0" />,
  [FlagSeverity.FOLLOWUP]: <FiRefreshCw className="w-[15px] h-[15px] text-blue-500  mt-0.5 shrink-0" />,
  [FlagSeverity.LEGAL]: <LuFileWarning className="w-[15px] h-[15px] text-red-600   mt-0.5 shrink-0" />,
};

const TAG_CLS: Record<TagVariant, string> = {
  [TagVariant.DANGER]: "bg-red-50  text-red-600  border-red-200",
  [TagVariant.INFO]: "bg-blue-50 text-blue-600 border-blue-200",
  [TagVariant.NEUTRAL]: "bg-gray-100 text-gray-500 border-gray-200",
};

function NegotiationTag({ tag }: { tag: TagItem }) {
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded border border-solid ${TAG_CLS[tag.variant]}`}>
      {tag.label}
    </span>
  );
}

function SkeletonLine({ width, height = 10 }: { width: string; height?: number }) {
  return <div className="bg-gray-200 rounded" style={{ width, height }} />;
}

function PanelSkeleton() {
  return (
    <div className="flex-1 overflow-hidden px-2 py-2 space-y-2">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <TbSparkles className="w-3.5 h-3.5 text-indigo-400 animate-pulse" />
        <span className="text-[11px] text-indigo-400 animate-pulse">Analysing thread...</span>
      </div>

      {/* Skeleton — counter-like */}
      <div className="bg-white rounded-lg px-3 py-3 space-y-2 animate-pulse">
        <SkeletonLine width="60%" height={12} />
        <SkeletonLine width="40%" height={10} />
      </div>

      {/* Skeleton — flags-like */}
      <div className="bg-white rounded-lg px-4 py-4 space-y-3 animate-pulse">
        <div className="flex items-center justify-between">
          <SkeletonLine width="18%" height={9} />
          <SkeletonLine width="14%" height={9} />
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className="w-[15px] h-[15px] rounded-full bg-gray-200 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1.5">
              <SkeletonLine width="80%" height={10} />
              <SkeletonLine width="55%" height={8} />
            </div>
          </div>
        ))}
      </div>

      {/* Skeleton — money/capacity-like */}
      <div className="bg-white rounded-lg px-4 py-4 space-y-2.5 animate-pulse">
        <SkeletonLine width="35%" height={9} />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <SkeletonLine width="40%" height={9} />
            <SkeletonLine width="22%" height={9} />
          </div>
        ))}
        <div className="mt-1 rounded-lg bg-gray-100 h-10" />
      </div>
    </div>
  );
}

// ─── Error ────────────────────────────────────────────────────────────────────

function PanelError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
        <TbAlertCircle className="w-5 h-5 text-red-400" />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-700">Analysis failed</p>
        <p className="text-[11px] text-gray-400 leading-relaxed">{message}</p>
      </div>
      <button
        onClick={onRetry}
        className="flex items-center gap-1.5 text-xs font-medium text-indigo-500 hover:text-indigo-700 transition-colors mt-1 cursor-pointer"
      >
        <FiRefreshCw className="w-3.5 h-3.5" />
        Retry
      </button>
    </div>
  );
}

function PanelNotFound() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
        <TbFileOff className="w-5 h-5 text-gray-400" />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-600">No analysis available</p>
        <p className="text-[11px] text-gray-400 leading-relaxed">
          {`There isn't enough context in this thread to generate a negotiation panel yet.`}
        </p>
      </div>
    </div>
  );
}

function CounterCardView({ card }: { card: CounterCard }) {
  const hasAmounts = card.ourAmount !== null || card.theirAmount !== null;
  return (
    <div className="bg-amber-50 border border-solid border-amber-200 rounded-lg px-3 py-2.5">
      <p className="text-sm font-semibold text-amber-900">{card.title}</p>
      {hasAmounts && (
        <p className="text-xs text-amber-700 mt-0.5">
          Theirs {card.theirAmount !== null ? formatUSD(card.theirAmount, card.currency) : "—"} · ours{" "}
          {card.ourAmount !== null ? formatUSD(card.ourAmount, card.currency) : "—"}
        </p>
      )}
    </div>
  );
}

function FlagsCardView({ card }: { card: FlagsCard }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Flags</span>
        <span className="text-[11px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
          {card.flags.length} open
        </span>
      </div>
      <div className="space-y-3">
        {card.flags.map((flag) => (
          <div key={flag.id} className="flex items-start gap-2.5">
            {FLAG_ICON[flag.severity]}
            <div>
              <p className="text-xs font-medium text-gray-800 leading-snug">{flag.title}</p>
              <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{flag.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CaseSummaryCardView({ card }: { card: CaseSummaryCard }) {
  return (
    <div>
      <p className="text-[11px] text-gray-400 mb-1.5">{card.sectionLabel}</p>
      <p className="text-sm font-semibold text-gray-900">
        {card.entityName} · {card.fileNumber}
      </p>
      <p className="text-[11px] text-gray-500 mt-0.5">{card.funderLabel}</p>
      <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">{card.description}</p>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {card.tags.map((tag, i) => (
          <NegotiationTag key={i} tag={tag} />
        ))}
      </div>
    </div>
  );
}

function MoneyCapacityCardView({ card }: { card: MoneyCapacityCard }) {
  const hasWarnings = card.warnings && card.warnings.length > 0;
  return (
    <div>
      <p className="text-[11px] text-gray-400 mb-2">{card.sectionLabel}</p>
      <div className="space-y-2">
        {(card.rows ?? []).map(({ label, value }, i) => (
          <div key={label} className="flex flex-wrap items-center justify-between">
            <span className="text-xs text-gray-500">{label}</span>
            <span className="text-xs font-medium text-gray-800">{value}</span>
          </div>
        ))}
      </div>
      {card.note && (
        <div className="mt-3 bg-amber-50 border border-solid border-amber-200 rounded-lg px-3 py-2">
          <p className="text-xs text-amber-800 leading-relaxed">{card.note}</p>
        </div>
      )}
      {hasWarnings && (
        <div className="mt-3 bg-amber-50 border border-solid border-amber-200 rounded-lg px-3 py-2.5">
          <div className="flex items-center gap-1.5 mb-2">
            <TbAlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <span className="text-[11px] font-medium text-amber-700">Data caveats</span>
          </div>
          <ul className="space-y-2 pl-5 list-disc">
            {(card.warnings ?? []).map((warning, i) => (
              <li key={i} className="text-[11px] text-amber-800 leading-relaxed">
                {warning}

                {/* <p className="text-[11px] text-amber-800 leading-relaxed">{warning}</p> */}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BulletListCardView({ card }: { card: BulletListCard }) {
  if (card.variant === BulletListVariant.WARNING) {
    return (
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <TbAlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
          <p className="text-[11px] font-medium text-red-500">{card.title}</p>
        </div>
        <ul className="list-disc pl-5 space-y-1.5 bg-red-50 rounded-md px-3 py-2.5 [&>li]:marker:text-red-400">
          {card.items.map((item, i) => (
            <li key={i} className="text-xs text-red-700 leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div>
      <p className="text-[11px] font-medium text-gray-500 mb-2">{card.title}</p>
      <ul className="list-disc pl-5 space-y-1.5 [&>li]:marker:text-gray-400">
        {card.items.map((item, i) => (
          <li key={i} className="text-xs text-gray-600 leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function renderCard(card: SmartNegotiationCard): React.ReactNode {
  switch (card.type) {
    case CardType.COUNTER:
      return <CounterCardView card={card} />;
    case CardType.FLAGS:
      return <FlagsCardView card={card} />;
    case CardType.CASE_SUMMARY:
      return <CaseSummaryCardView card={card} />;
    case CardType.MONEY_CAPACITY:
      return <MoneyCapacityCardView card={card} />;
    case CardType.BULLET_LIST:
      return <BulletListCardView card={card} />;
  }
}

function CardsList({ cards }: { cards: SmartNegotiationCard[] }) {
  return (
    <div className="flex-1 overflow-y-auto space-y-2 px-2 py-2">
      {cards.filter(shouldRenderCard).map((card) =>
        card.type === CardType.COUNTER ? (
          <div key={card.id}>{renderCard(card)}</div>
        ) : (
          <div key={card.id} className="px-4 py-4 bg-white rounded-lg">
            {renderCard(card)}
          </div>
        ),
      )}
    </div>
  );
}

interface SmartNegotiationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  negotiationContext: {
    application_id?: string;
    loan_id?: string;
    thread_id?: string;
    message_id?: string;
  };
  isShowInDrawer: boolean;
}

function ReanalysisBanner() {
  return (
    <div className="flex items-center gap-1.5 mt-2 bg-indigo-50 border border-solid border-indigo-100 rounded-md px-2.5 py-1.5">
      <TbSparkles className="w-3 h-3 text-indigo-400 animate-pulse shrink-0" />
      <span className="text-[11px] text-indigo-500">Analysing new thread...</span>
    </div>
  );
}

function SmartNegotiationPanel({
  isOpen,
  onClose,
  negotiationContext,
  isShowInDrawer = false,
}: SmartNegotiationPanelProps) {
  const { data, isLoading, isError, error, refetch } = ApiService.negotiation.useGetSmartPanel(negotiationContext);

  const smartNegotiation = data?.result;
  const status = smartNegotiation?.status;
  const hasExistingCards = (smartNegotiation?.cards?.length ?? 0) > 0;

  // True when re-analysing but prior card data already exists to show
  const isReanalysing = status === PanelStatus.PROCESSING && hasExistingCards;

  function renderBody() {
    if (isLoading) return <PanelSkeleton />;
    if (isError) return <PanelError message={getErrorMessage(error)} onRetry={refetch} />;
    if (status === PanelStatus.NOT_FOUND) return <PanelNotFound />;

    // PROCESSING with no prior data → full skeleton
    if (status === PanelStatus.PROCESSING && !hasExistingCards) return <PanelSkeleton />;

    // PROCESSING with prior data, or COMPLETED → render cards
    if ((status === PanelStatus.PROCESSING || status === PanelStatus.COMPLETED) && smartNegotiation?.cards) {
      return <CardsList cards={smartNegotiation.cards} />;
    }

    if (status === PanelStatus.ERROR || status === PanelStatus.STALLED) {
      return <PanelError message="Something went wrong while analysing this thread. Please retry." onRetry={refetch} />;
    }

    return null;
  }
  if (isShowInDrawer) {
    return (
      <Drawer
        open={isOpen}
        onClose={onClose}
        placement="right"
        width={400}
        title={
          <div className="flex items-center gap-1.5">
            <TbSparkles className="w-3.5 h-3.5 text-indigo-500" />
            <span className="text-sm font-semibold text-gray-900">{"Suggestions"}</span>
          </div>
        }
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "#f3f4f6",
          },
        }}
      >
        {isReanalysing && (
          <div className="px-4 pt-3 pb-3 shrink-0 border-b border-solid border-gray-200 bg-white">
            {isReanalysing && <ReanalysisBanner />}
          </div>
        )}
        {renderBody()}
      </Drawer>
    );
  }

  return (
    <aside
      style={{
        width: isOpen ? 400 : 0,
        minWidth: isOpen ? 400 : 0,
        transition: "width 0.2s ease, min-width 0.2s ease",
      }}
      className="h-full flex flex-col bg-gray-100 border-l border-solid border-gray-200 overflow-hidden"
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-solid border-gray-100 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <TbSparkles className="w-3.5 h-3.5 text-indigo-500" />
            <h2 className="text-sm font-semibold text-gray-900">{`Suggestions`}</h2>
          </div>
          <Button onClick={onClose} danger size="small" icon={<TbX />} />

          {/* <TbX onClick={onClose} className="cursor-pointer text-red-400 hover:text-gray-600 transition-colors" /> */}
        </div>
        {/* {smartNegotiation?.subtitle && (
          <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">{smartNegotiation.subtitle}</p>
        )} */}

        {/* Re-analysis banner — only shown when prior cards exist */}
        {isReanalysing && (
          <div className="flex items-center gap-1.5 mt-2 bg-indigo-50 border border-solid border-indigo-100 rounded-md px-2.5 py-1.5">
            <TbSparkles className="w-3 h-3 text-indigo-400 animate-pulse shrink-0" />
            <span className="text-[11px] text-indigo-500">Analysing new thread...</span>
          </div>
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      {renderBody()}
    </aside>
  );
}

export default SmartNegotiationPanel;
